import React, {useContext, useEffect} from 'react'

import Context from '@s-ui/react-context'

const getAnalytics = () =>
  new Promise(resolve => {
    // We check first if we have the UMD version of Segment Wrapper,
    // so that we return it instead of importing a new version.
    if (window.motor?.analytics) resolve(window.motor?.analytics)
    resolve(import('@adv-mt/mt-segment-wrapper').then(({default: analytics}) => analytics))
  })

const dispatchTrackV2 = async ({eventName, event, data, identify, reset, signedOutEventName}) => {
  const isAnalyticsLoaded = window.analytics
  if (!isAnalyticsLoaded) return
  const analytics = await getAnalytics()

  // Migrating this parameteer in order to use only 'event'
  // 'eventName' was used in legacy tagging plan
  if (eventName) {
    // eslint-disable-next-line no-console
    console.warn('withEventTrack: "eventName" is deprecated, please use event instead.')
  }

  const resetActions = () => {
    analytics.track(signedOutEventName)
    analytics.reset()
  }

  event = event || eventName
  identify && analytics.identify(identify.userId, identify.data)
  reset && resetActions()
  event && analytics.track(event, data)
}

const dispatchTracks = ({tracks, trackingData, signedOutEventName}) =>
  tracks.forEach(({target, ...params}) => {
    const trackParams = {...params, data: {...params?.data, ...trackingData}}
    return dispatchTrackV2({...trackParams, signedOutEventName})
  })

// eslint-disable-next-line react/prop-types
const TrackingWithEventTrack = ({tracks}) => {
  useEffect(() => {
    dispatchTracks({tracks})
  }, [tracks])

  return null
}

/**
 * @param {Object} BaseComponent
 * @param {Array|String} params
 * @param {String} params[0].eventName
 * @param {Object} params[0].data
 * @param {String} params[0].target
 * @param {String} trigger
 */
const TrackingWithEventTrackHoc = (BaseComponent, params, trigger = 'onClick') => {
  if (typeof window === 'undefined' || !params) return BaseComponent

  const trackingEventProps = {}

  const dispatchAllTracks = ({trackingData, signedOutEventName}) => {
    dispatchTracks({tracks: params, trackingData, signedOutEventName})
  }

  const dispatchTrack = ({trackingData, signedOutEventName}) =>
    typeof params === 'string'
      ? dispatchTrackV2({
          eventName: params,
          data: trackingData,
          signedOutEventName
        })
      : dispatchAllTracks({trackingData, signedOutEventName})

  const WithEventTrack = props => {
    const {domain} = useContext(Context)
    const {
      TRACKING_DATA_BY_SITE,
      TRACKING_EVENT_NAMES: {SIGNED_OUT: signedOutEventName}
    } = domain.get('config').get('tracking')

    trackingEventProps[trigger] = ev => {
      dispatchTrack({trackingData: TRACKING_DATA_BY_SITE, signedOutEventName})
      props.hasOwnProperty(trigger) && props[trigger](ev) // eslint-disable-line
    }

    return <BaseComponent {...props} {...trackingEventProps} />
  }

  const displayName = BaseComponent.displayName

  WithEventTrack.displayName = `WithEventTrack(${displayName})`

  return WithEventTrack
}

TrackingWithEventTrackHoc.displayName = 'TrackingWithEventTrackHoc'

export default TrackingWithEventTrackHoc
export {TrackingWithEventTrackHoc, TrackingWithEventTrack}
